import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibSpinningLoader } from '@chewy/kib-overlays-react'
import { DateUtils, moment, type Nil, Utils } from '@pbt/pbt-ui-components'
import ResponsibilityName from '@pbt/pbt-ui-components/src/constants/responsibilityNames'

import PanelAccordion from '~/components/common/PanelAccordion'
import DialogNames from '~/constants/DialogNames'
import { fetchAppointment } from '~/store/actions/timetable'
import {
  getTimetableEvent,
  getTimetableIsLoading,
} from '~/store/reducers/timetable'
import { addOriginalBusinessId } from '~/utils'
import useDialog from '~/utils/useDialog'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-overlays-styles/dist/styles/kib-spinning-loader.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'
import styles from './AppointmentDetailsTab.module.scss'

interface AppointmentDetailsTabProps {
  appointmentId: string | Nil
  businessId?: string | Nil
  soapId?: string | Nil
}

const AppointmentDetailsTab = ({
  soapId,
  appointmentId,
  businessId,
}: AppointmentDetailsTabProps) => {
  const { t } = useTranslation(['Communications', 'Common'])
  const dispatch = useDispatch()
  const appointment = useSelector(getTimetableEvent(appointmentId))
  const isLoading = useSelector(getTimetableIsLoading)
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(true)
  const isPastAppointment = moment().isAfter(
    appointment?.scheduledStartDatetime,
    'days',
  )
  const appointmentSoapId = appointment?.soapId || soapId
  const [openAppointmentDialog] = useDialog(DialogNames.EVENT)

  useEffect(() => {
    if (appointmentId && !appointment) {
      dispatch(fetchAppointment(appointmentId))
    }
  }, [appointmentId, appointment, dispatch])

  const assignedPersons = useMemo(() => {
    const personResponsibilities = appointment?.personResponsibilities || []
    const personRoles = appointment?.personRoles || []

    if (personResponsibilities.length > 0) {
      return personResponsibilities
        .filter(
          (pr) =>
            pr.responsibility?.name === ResponsibilityName.Veterinarian ||
            pr.responsibility?.name === ResponsibilityName.VetTech,
        )
        .map((pr) => Utils.getPersonString(pr.person))
        .filter(Boolean)
    }

    if (personRoles.length > 0) {
      return personRoles
        .map((pr) => Utils.getPersonString(pr.person))
        .filter(Boolean)
    }

    return []
  }, [appointment])

  const formattedDateTime = useMemo(() => {
    if (!appointment?.scheduledStartDatetime) {
      return ''
    }
    const dateMoment = moment(appointment.scheduledStartDatetime)
    const dayName = dateMoment.format('dddd')
    const date = dateMoment.format('M/D/YYYY')
    const startTime = DateUtils.formatTime(appointment.scheduledStartDatetime)

    return `${dayName} ${date} at ${startTime}`
  }, [appointment?.scheduledStartDatetime])

  const handleViewAppointment = () => {
    if (isPastAppointment && appointmentSoapId) {
      navigate(
        addOriginalBusinessId(
          `/soap/${appointmentSoapId}/tab/soap`,
          businessId,
        ),
      )
    } else if (appointmentId) {
      openAppointmentDialog({ appointmentId })
    }
  }

  if (!appointmentId && !soapId) {
    return null
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <PanelAccordion
            noPadding
            className={styles.accordion}
            expanded={expanded}
            titleComponent={
              <span className={styles.title}>
                {t('Communications:APPOINTMENT_DETAILS_TAB.TITLE')}
              </span>
            }
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
          >
            <div className={styles.content}>
              <div className={styles.loaderContainer}>
                <KibSpinningLoader embedded />
              </div>
            </div>
          </PanelAccordion>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <PanelAccordion
          noPadding
          className={styles.accordion}
          expanded={expanded}
          titleComponent={
            <span className={styles.title}>
              {t('Communications:APPOINTMENT_DETAILS_TAB.TITLE')}
            </span>
          }
          onChange={(_, isExpanded) => setExpanded(isExpanded)}
        >
          <div className={styles.content}>
            {assignedPersons.length > 0 && (
              <div className={styles.detailRow}>
                <span className={styles.assigned}>
                  {`${t('Communications:APPOINTMENT_DETAILS_TAB.ASSIGNED')}: `}
                  {assignedPersons.join(', ')}
                </span>
              </div>
            )}

            {formattedDateTime && (
              <div className={styles.detailRow}>
                <span className={styles.label}>
                  {`${t('Communications:APPOINTMENT_DETAILS_TAB.WHEN')}: `}
                </span>
                <span className={styles.value}>{formattedDateTime}</span>
              </div>
            )}

            {appointment?.notes && (
              <div className={styles.detailRow}>
                <span className={styles.label}>
                  {`${t('Communications:APPOINTMENT_DETAILS_TAB.REASON_FOR_VISIT')}: `}
                </span>
                <span className={styles.value}>{appointment.notes}</span>
              </div>
            )}

            <div className={styles.buttonContainer}>
              <KibButtonNew
                emphasis="secondary"
                size="medium"
                theme="utility"
                onClick={handleViewAppointment}
              >
                {isPastAppointment && appointmentSoapId
                  ? t('Communications:APPOINTMENT_DETAILS_TAB.VIEW_SOAP')
                  : t(
                      'Communications:APPOINTMENT_DETAILS_TAB.VIEW_APPOINTMENT',
                    )}
              </KibButtonNew>
            </div>
          </div>
        </PanelAccordion>
      </div>
    </div>
  )
}

export default AppointmentDetailsTab
